import React, { useEffect, useMemo, useRef, useState } from 'react';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ProgrammeProgressPayments from './ProgrammeProgressPayments';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import { fetchUsers } from '../services/user';
import { getStudentProgressAdmin, getStudentSummaryAdmin } from '../services/analytics';

const progressPalette = {
  success: { color: '#087443', background: '#E8F8EF', border: '#02A552' },
  warning: { color: '#704500', background: '#FFF4D6', border: '#D99300' },
  error: { color: '#9B1C1C', background: '#FEE2E2', border: '#C9362B' },
  neutral: { color: '#334155', background: '#E8EDF5', border: '#CBD5E1' },
};

const formatDuration = (minutes) => {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return 'Not recorded';
  const hours = Math.floor(value / 60);
  const remainder = value % 60;
  return [hours ? `${hours}h` : '', remainder ? `${remainder}m` : ''].filter(Boolean).join(' ');
};

const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const ProgressStatus = ({ status }) => {
  const value = status || { label: 'Not enough data', symbol: 'i', tone: 'neutral', description: 'A tutor assessment is required.' };
  const palette = progressPalette[value.tone] || progressPalette.neutral;
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      aria-label={`${value.label}. ${value.description || ''}`.trim()}
      sx={{ border: `1px solid ${palette.border}`, bgcolor: palette.background, color: palette.color, borderRadius: 99, px: 1.5, py: 0.75, width: 'fit-content' }}
    >
      <Typography component="span" fontWeight={900} aria-hidden="true">{value.symbol || 'i'}</Typography>
      <Typography component="span" variant="caption" fontWeight={800}>{value.label || 'Not enough data'}</Typography>
    </Stack>
  );
};

const ReportSection = ({ label, value }) => (
  <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid #eef2f7' }}>
    <Typography variant="caption" fontWeight={800} color="#02A552" sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.65 }}>{value}</Typography>
  </Box>
);

const PrintStyles = () => (
  <style>{`
    @media print {
      body { background: #fff !important; }
      .no-print { display: none !important; }
      .report-sheet { width: 100%; padding: 0; box-shadow: none !important; }
      .report-card { break-inside: avoid; box-shadow: none !important; border: 1px solid #e5e7eb; }
    }
    .report-sheet { max-width: 960px; margin: 0 auto; padding: 24px; background: #fff; }
    .report-title { font-size: 1.6rem; color: #00154A; font-weight: 800; letter-spacing: .2px; }
    .report-meta { color: #64748b; font-size: .9rem; }
    .report-card { background:#fff; border-radius: 18px; box-shadow: 0 5px 12px rgba(0,0,0,.06); padding: 16px; }
    .stat { font-size: 1.6rem; font-weight: 800; color:#0b245e; }
    .muted { color:#64748b; }
  `}</style>
);

const Reports = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);

  const sheetRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const all = await fetchUsers('student');
        if (isMounted) {
          setStudents(all);
        }
      } catch (e) {
        if (isMounted) {
          setError(e?.message || 'Failed to load students');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const selectedStudent = useMemo(
    () => students.find((student) => student._id === selectedId),
    [students, selectedId]
  );

  const loadReport = async () => {
    if (!selectedId) return;
    try {
      setBusy(true);
      setError('');
      setReport(null);
      setProgress(null);
      const [data, progressData] = await Promise.all([
        getStudentSummaryAdmin(selectedId),
        getStudentProgressAdmin(selectedId),
      ]);
      setReport(data);
      setProgress(progressData);
    } catch (e) {
      setError(e?.message || 'Failed to load report');
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    window.print();
  };

  const studentOptions = useMemo(
    () =>
      students.map((student) => ({
        value: student._id,
        label: `${student.name || 'Unnamed'} ${student.surname || ''}`.trim(),
        email: student.email,
      })),
    [students]
  );

  return (
    <DashboardLayout>
      <PrintStyles />
      <Stack spacing={3} className="no-print">
        <Card>
          <CardHeader
            title={
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AssessmentRoundedIcon color="primary" />
              </Stack>
            }
            subheader="Generate learner performance summaries."
          />
          <Divider />
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <FormControl fullWidth sx={{ minWidth: { md: 280 } }}>
                <InputLabel id="student-select-label">Student</InputLabel>
                <Select
                  labelId="student-select-label"
                  label="Student"
                  value={selectedId}
                  onChange={(event) => setSelectedId(event.target.value)}
                  disabled={loading || busy}
                >
                  <MenuItem value="">
                    <em>Select a student…</em>
                  </MenuItem>
                  {studentOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="column" spacing={0.25}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.label || option.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                {!selectedId ? <FormHelperText>Select a student to enable report actions.</FormHelperText> : null}
              </FormControl>

              <AdminActionGroup>
                <Button
                  variant="contained"
                  startIcon={busy ? <RefreshRoundedIcon /> : <PlayArrowRoundedIcon />}
                  onClick={loadReport}
                  disabled={!selectedId || busy}
                >
                  {busy ? 'Generating…' : 'Generate'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearRoundedIcon />}
                  onClick={() => { setReport(null); setProgress(null); }}
                  disabled={busy || !report}
                >
                  Clear
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfRoundedIcon />}
                  onClick={downloadPdf}
                  disabled={!report}
                >
                  Download PDF
                </Button>
              </AdminActionGroup>
            </Stack>
          </CardContent>
          {(loading || busy) && <LinearProgress />}
        </Card>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Stack>

      {report && (
        <Paper ref={sheetRef} className="report-sheet" sx={{ mt: { xs: 2, md: 4 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mb: 3 }}
          >
            <Stack spacing={0.75}>
              <Typography className="report-title">Learner Performance Report</Typography>
              <Typography className="report-meta">Generated: {new Date().toLocaleString()}</Typography>
              <Typography className="report-meta">
                Student: {report.student?.name || selectedStudent?.name || 'Unknown'} ({
                  report.student?.email || selectedStudent?.email || 'No email'
                })
              </Typography>
            </Stack>
            <Box component="img" src="/lms-logo.png" alt="MyApex LMS" sx={{ width: { xs: 190, sm: 240 }, height: 'auto' }} />
          </Stack>

          <ProgrammeProgressPayments programmes={progress?.programmes || []} />
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {progress?.summary?.allocation?.paymentVerified ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper className="report-card" sx={{ height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sessions Purchased</Typography>
                    <Typography className="stat">{progress.summary.allocation.purchased}</Typography>
                    <Typography className="muted">Paid allocation</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper className="report-card" sx={{ height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Verified Completed</Typography>
                    <Typography className="stat">{progress.summary.allocation.completed}</Typography>
                    <Typography className="muted">Reported and attendance verified</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper className="report-card" sx={{ height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sessions Remaining</Typography>
                    <Typography className="stat">{progress.summary.allocation.remaining}</Typography>
                    <Typography className="muted">Purchased minus verified completed</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper className="report-card" sx={{ height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Needs Verification</Typography>
                    <Typography className="stat">{progress.summary.allocation.unverifiedPast}</Typography>
                    <Typography className="muted">Past linked lessons not deducted</Typography>
                  </Paper>
                </Grid>
              </>
            ) : null}
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Overall Sessions
                </Typography>
                <Typography className="stat">{report.totalSessions}</Typography>
                <Typography className="muted">Total sessions</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Upcoming Sessions
                </Typography>
                <Typography className="stat">{report.totalUpcomingClasses}</Typography>
                <Typography className="muted">Next 5 listed below</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Study Hours
                </Typography>
                <Typography className="stat">{report.totalStudyHours} hrs</Typography>
                <Typography className="muted">Cumulative</Typography>
              </Paper>
            </Grid>
            {progress?.summary?.averageUnderstandingScore != null ? (
              <Grid item xs={12} sm={6} md={4}>
                <Paper className="report-card" sx={{ height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Average Understanding</Typography>
                  <Typography className="stat">{progress.summary.averageUnderstandingScore}/5</Typography>
                  <Typography className="muted">Tutor-assessed lesson understanding</Typography>
                </Paper>
              </Grid>
            ) : null}
            {progress?.summary?.homeworkReviewed ? (
              <Grid item xs={12} sm={6} md={4}>
                <Paper className="report-card" sx={{ height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Homework Completion</Typography>
                  <Typography className="stat">{progress.summary.homeworkCompletionRate}%</Typography>
                  <Typography className="muted">{progress.summary.homeworkCompleted} of {progress.summary.homeworkReviewed} reviewed</Typography>
                </Paper>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Reported Lessons</Typography>
                <Typography className="stat">{progress?.summary?.reportedLessons ?? 0}</Typography>
                <Typography className="muted">Structured tutor reports</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Recorded Attendance</Typography>
                <Typography className="stat">{progress?.summary?.attendanceRate == null ? '-' : `${progress.summary.attendanceRate}%`}</Typography>
                <Typography className="muted">{progress?.summary?.attendanceRecorded || 0} attendance record(s)</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Reported Learning Time</Typography>
                <Typography className="stat">{formatDuration(progress?.summary?.durationMinutes)}</Typography>
                <Typography className="muted">Completed reported lessons only</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Stack spacing={2.5}>
            {progress?.bookings?.length ? (
              <Paper className="report-card">
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Package Allocation</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Booking</TableCell>
                        <TableCell>Package</TableCell>
                        <TableCell>Purchased</TableCell>
                        <TableCell>Completed</TableCell>
                        <TableCell>Upcoming</TableCell>
                        <TableCell>Remaining</TableCell>
                        <TableCell>Needs review</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {progress.bookings.map((booking) => (
                        <TableRow key={booking.bookingId}>
                          <TableCell>{booking.bookingNumber || booking.bookingId}</TableCell>
                          <TableCell>{booking.packageName || '-'}</TableCell>
                          <TableCell>{booking.allocation?.purchased ?? 0}</TableCell>
                          <TableCell>{booking.allocation?.completed ?? 0}</TableCell>
                          <TableCell>{booking.allocation?.upcoming ?? 0}</TableCell>
                          <TableCell>{booking.allocation?.remaining ?? '-'}</TableCell>
                          <TableCell>{booking.allocation?.unverifiedPast ?? 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : null}
            <Paper className="report-card">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Progress Status</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{progress?.summary?.progressStatus?.description || 'A tutor assessment is required before learning progress can be rated.'}</Typography>
                </Box>
                <ProgressStatus status={progress?.summary?.progressStatus} />
              </Stack>
            </Paper>

            <Paper className="report-card">
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Subject Progress</Typography>
              {progress?.subjectBreakdown?.length ? (
                <Grid container spacing={1.5}>
                  {progress.subjectBreakdown.map((subject) => (
                    <Grid item xs={12} sm={6} md={4} key={subject.subject}>
                      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 3, p: 1.5, height: '100%' }}>
                        <Typography fontWeight={800} color="#00154A">{subject.subject}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ my: 1 }}>
                          {subject.reportedLessons} reports · {subject.attendanceRate == null ? 'attendance not recorded' : `${subject.attendanceRate}% attendance`}
                        </Typography>
                        <ProgressStatus status={subject.progressStatus} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : <Typography className="muted">No structured subject reports recorded.</Typography>}
            </Paper>

            <Paper className="report-card">
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Lesson Reports</Typography>
              {!progress?.lessons?.length ? (
                <Typography className="muted">No submitted lesson reports.</Typography>
              ) : (
                <Stack spacing={1.5}>
                  {progress.lessons.map((lesson) => (
                    <Box key={lesson.sessionId} sx={{ border: '1px solid #e5e7eb', borderRadius: 3, p: { xs: 1.5, sm: 2 } }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Box>
                          <Typography fontWeight={800} color="#00154A">{lesson.subject}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[lesson.tutorName, lesson.startTime ? new Date(lesson.startTime).toLocaleString('en-ZA') : null, formatDuration(lesson.durationMinutes)].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {lesson.attendanceStatus && lesson.attendanceStatus !== 'not_recorded' ? <Chip size="small" label={`Attendance: ${humanize(lesson.attendanceStatus)}`} /> : null}
                          {lesson.engagement && lesson.engagement !== 'not_assessed' ? <Chip size="small" color="success" variant="outlined" label={`Engagement: ${humanize(lesson.engagement)}`} /> : null}
                          {lesson.understandingScore ? <Chip size="small" color="success" label={`Understanding: ${lesson.understandingScore}/5`} /> : null}
                          {lesson.homeworkReviewStatus && !['not_reviewed', 'not_applicable'].includes(lesson.homeworkReviewStatus) ? <Chip size="small" variant="outlined" label={`Homework: ${humanize(lesson.homeworkReviewStatus)}`} /> : null}
                        </Stack>
                      </Stack>
                      {lesson.tutorNotes ? <ReportSection label={lesson.detailedTutorNotes ? 'Detailed tutor notes' : 'Lesson summary'} value={lesson.tutorNotes} /> : null}
                      {lesson.behaviourRatings && Object.keys(lesson.behaviourRatings).length ? (
                        <ReportSection
                          label="Learner behaviour"
                          value={Object.entries(lesson.behaviourRatings)
                            .filter(([, rating]) => rating && rating !== 'not_assessed')
                            .map(([dimension, rating]) => `${humanize(dimension)}: ${humanize(rating)}`)
                            .join(' · ')}
                        />
                      ) : null}
                      {lesson.lessonObjectives?.length ? <ReportSection label="Lesson objectives" value={lesson.lessonObjectives.join(' · ')} /> : null}
                      {lesson.topicsCovered?.length ? <ReportSection label="Topics covered" value={lesson.topicsCovered.join(' · ')} /> : null}
                      {lesson.subjectSkillAssessments?.length ? (
                        <ReportSection
                          label="Subject skill assessments"
                          value={lesson.subjectSkillAssessments.map((assessment) => `${assessment.skill}: ${humanize(assessment.rating)}${assessment.notes ? ` (${assessment.notes})` : ''}`).join(' · ')}
                        />
                      ) : null}
                      {lesson.homeworkAssigned ? <ReportSection label="Homework assignment" value={`${lesson.homeworkAssigned}${lesson.homeworkDueAt ? ` · Due ${new Date(lesson.homeworkDueAt).toLocaleDateString('en-ZA')}` : ''}`} /> : null}
                      {lesson.homeworkReviewNotes ? <ReportSection label="Homework review" value={lesson.homeworkReviewNotes} /> : null}
                      {lesson.improvementAreas?.length ? <ReportSection label="Areas to improve" value={lesson.improvementAreas.join(' · ')} /> : null}
                      {lesson.nextLessonFocus ? <ReportSection label="Next lesson focus" value={lesson.nextLessonFocus} /> : null}
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper className="report-card">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Monthly Study Hours
              </Typography>
              {(!report.monthlyStudyHours || report.monthlyStudyHours.length === 0) ? (
                <Typography className="muted">No data</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell>Hours</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.monthlyStudyHours.map((month) => (
                        <TableRow key={month.month}>
                          <TableCell>{month.month}</TableCell>
                          <TableCell>{month.hours}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            <Paper className="report-card">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Upcoming Classes
              </Typography>
              {(!report.upcomingClasses || report.upcomingClasses.length === 0) ? (
                <Typography className="muted">No upcoming classes</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Subject</TableCell>
                        <TableCell>Tutor</TableCell>
                        <TableCell>Start</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.upcomingClasses.map((session) => (
                        <TableRow key={session._id}>
                          <TableCell>{session.subject || '-'}</TableCell>
                          <TableCell>{session.tutorId?.name || '-'}</TableCell>
                          <TableCell>{new Date(session.startTime).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Stack>
        </Paper>
      )}
    </DashboardLayout>
  );
};

export default Reports;
